import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { X, Loader2, Search } from 'src/components/common/icons';
import { ILeaseTin } from './Maintenance1099Modal';
import useLatestRequest from '../useLatestRequest';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (tinId: number) => void;
}

const ScanByNameModal: React.FC<IProps> = ({ isOpen, onClose, onSelect }) => {
    const { get, response } = useFetch();
    const [scanNameInput, setScanNameInput] = useState('');
    const [scanResults, setScanResults] = useState<ILeaseTin[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const scanRequests = useLatestRequest();

    useEffect(() => {
        if (!isOpen) {
            setScanNameInput('');
            setScanResults([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            scanRequests.invalidate();
            return undefined;
        }
        const trimmedInput = scanNameInput.trim();
        if (trimmedInput.length < 3) {
            setScanResults([]);
            setIsScanning(false);
            return undefined;
        }

        const delayDebounceFn = setTimeout(async () => {
            const requestId = scanRequests.begin();
            setIsScanning(true);
            const results = await get(`/v1.0/leases/tins/scan-by-name?name=${encodeURIComponent(trimmedInput)}`);
            if (!scanRequests.isCurrent(requestId)) {
                return;
            }
            if (response.ok && Array.isArray(results)) {
                setScanResults(results as ILeaseTin[]);
            } else {
                setScanResults([]);
            }
            setIsScanning(false);
        }, 1000);

        return () => {
            clearTimeout(delayDebounceFn);
            scanRequests.invalidate();
        };
    }, [scanNameInput, isOpen, get, response, scanRequests]);

    const formatMoney = (val?: number | null) =>
        val !== null && val !== undefined ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
    const formatDate = (val?: string | null) => (val ? new Date(val).toLocaleDateString() : '');

    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={styles.subModalOverlay}
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div style={styles.subModalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Scan Names</h3>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>
                <div style={styles.modalBody}>
                    <div style={styles.inputRow}>
                        <label style={styles.label}>TIN Name</label>
                        <div style={{ position: 'relative', width: '300px' }}>
                            <Search style={styles.searchIcon} />
                            <LeaseRecordInput
                                style={styles.inputWithIcon}
                                value={scanNameInput}
                                onChange={(e) => setScanNameInput(e.target.value)}
                                placeholder="Start typing to scan..."
                                autoFocus={true}
                            />
                        </div>
                        {isScanning && <Loader2 className="animate-spin" style={styles.loaderIcon} />}
                    </div>
                    <div style={styles.tableWrapper}>
                        <table style={styles.tableBase}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={styles.tableHeaderCell}>Name</th>
                                    <th style={styles.tableHeaderCell}>TIN</th>
                                    <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Tax Year</th>
                                    <th style={styles.tableHeaderCell}>Type</th>
                                    <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Amount</th>
                                    <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Leases</th>
                                    <th style={styles.tableHeaderCell}>Trans. Date</th>
                                    <th style={styles.tableHeaderCell}>Correct Date</th>
                                    <th style={{ ...styles.tableHeaderCell, textAlign: 'center', width: '80px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {scanResults.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={styles.noResults}>
                                            {scanNameInput.trim().length === 0
                                                ? 'Type a name to begin scanning.'
                                                : scanNameInput.trim().length < 3
                                                ? 'Please enter at least 3 characters to search...'
                                                : isScanning
                                                ? 'Searching...'
                                                : 'No matches found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    scanResults.map((row: ILeaseTin, i: number) => (
                                        <tr key={i} style={styles.scanRow}>
                                            <td style={{ ...styles.tableDataCell, fontWeight: 500 }}>{row.name1}</td>
                                            <td style={styles.tableDataCell}>{row.tin}</td>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>{row.taxYear}</td>
                                            <td style={styles.tableDataCell}>{row.tinType}</td>
                                            <td style={{ ...styles.tableDataCell, fontWeight: 600, textAlign: 'right' }}>
                                                {formatMoney(row.amount)}
                                            </td>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>{row.leasesCount || 0}</td>
                                            <td style={styles.tableDataCell}>{formatDate(row.transmitDate)}</td>
                                            <td style={styles.tableDataCell}>{formatDate(row.correctDate)}</td>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                                                <button onClick={() => onSelect(row.tinId)} style={styles.selectButton}>
                                                    Open
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    subModalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        backdropFilter: 'blur(2px)',
    },
    subModalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '0.5rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '900px',
        height: '80vh',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
    },
    modalTitle: { margin: 0, fontSize: '1rem', color: '#111827', fontWeight: 600 },
    closeButton: { color: '#9CA3AF', padding: '0.25rem', borderRadius: '50%', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' },
    closeIcon: { width: '1rem', height: '1rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
    inputRow: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' },
    label: { fontSize: '0.875rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' },
    inputWithIcon: {
        padding: '0.5rem 0.75rem',
        paddingLeft: '2rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        color: '#111827',
        backgroundColor: '#FFFFFF',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    searchIcon: { position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: '#9CA3AF' },
    loaderIcon: { width: '1.25rem', height: '1.25rem', color: '#4F46E5' },
    tableWrapper: { overflowY: 'auto', flex: 1, minHeight: 0, borderRadius: '0.5rem', border: '1px solid #E5E7EB', backgroundColor: 'white' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    scanRow: { borderBottom: '1px solid #F3F4F6', transition: 'all 0.2s ease', cursor: 'pointer' },
    tableDataCell: { padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#111827' },
    selectButton: {
        backgroundColor: '#EFF6FF',
        color: '#4F46E5',
        border: '1px solid #BFDBFE',
        padding: '0.25rem 0.75rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    noResults: { padding: '1.5rem', textAlign: 'center', color: '#6B7280', fontSize: '0.875rem' },
};

export default ScanByNameModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ScanByNameModal.tsx